// Curated "Issue type" catalog for the Issues board's toolbar dropdown ONLY (client request,
// 2026-09-15). Six client-defined headings, each mapping to a fixed set of specific questions from
// the active "Welfare Hostel Inspection — v3 (issue triggers)" production form template
// (entity 8a0e7a37-0001-4000-8000-000000000001, template 89a52af0-cd5d-40ac-b2e6-53d21a7c90d1).
//
// Matching is by exact `question_text` (the issue feed carries no `question_id` — see
// `RawGisIssue` in `api/map-gis.ts`), so these strings must stay byte-for-byte identical to the
// live form template's `question_text` values. If a question is reworded in the form builder,
// update it here too or it silently drops out of this dropdown.
//
// UI shape (2026-09-15 correction): the dropdown itself shows ONLY the 6 headings, flat — same
// shape as every other toolbar dropdown. Drilling into a heading's specific questions happens via
// the existing "Group by → Question" rail, once selecting a heading has filtered the working set
// down to just its curated questions (see `toggleIssueType` in gisIssueViewStore.ts, which also
// defaults `groupBy` to 'question' when a heading is selected).
//
// Scope: this dropdown ONLY. The underlying `GisIssue.category` field (section_title) is
// untouched, so every other surface — Group by "Issue type", the Dashboard "Issues by type"
// donut, Related Issues, search — keeps showing the real form sections exactly as before.

export interface IssueTypeGroup {
  heading: string
  questions: readonly string[]
}

export const ISSUE_TYPE_CATALOG: readonly IssueTypeGroup[] = [
  {
    heading: 'Staff',
    questions: [
      'Whether the HWO / Principal / Special Officer present at time of inspection',
      'Cook — number of workers on pay roll',
      'Watchmen / Attender — number of workers on pay roll',
    ],
  },
  {
    heading: 'Infra',
    questions: [
      'Are proper lighting arrangements in place?',
      'Are CC cameras installed?',
      'Have any repairs (minor / major) been identified?',
    ],
  },
  {
    heading: 'Sanitation',
    questions: [
      'Are the dormitories clean and is mesh fixed on windows?',
      'Is waste water and drain water properly drained out?',
      'Are all existing bathrooms and toilets fitted with doors?',
      'Is the septic tank cleaned regularly?',
      'Is the premises maintained neatly?',
      'Nature of cooking fuel',
      'Are the kitchen and dining areas cleaned?',
      'Is the kitchen wash area clean and free from algae / foul smell?',
    ],
  },
  {
    heading: 'Drinking Water',
    questions: ['Is safe / purified drinking water available?'],
  },
  {
    heading: 'Medical',
    questions: ['Is the ANM available in the hostel (where applicable)?'],
  },
  {
    heading: 'Education',
    questions: ['Are boarders supplied with dresses?'],
  },
] as const

const QUESTION_TO_HEADING: ReadonlyMap<string, string> = new Map(
  ISSUE_TYPE_CATALOG.flatMap((g) => g.questions.map((q) => [q, g.heading] as const)),
)

/** The curated heading for a question's exact text, or null when the question isn't in any of the
 *  6 curated headings (hides it from the "Issue type" dropdown/facet — it still shows everywhere
 *  else, unaffected). */
export function issueTypeHeadingFor(question: string | null): string | null {
  return question === null ? null : (QUESTION_TO_HEADING.get(question) ?? null)
}
